import React, { useEffect, useState } from "react";
import {
    Box,
    Typography,
    Grid,
    Card,
    CardContent,
    Button,
    SvgIcon,
    Breadcrumbs,
    Link,
    CircularProgress,
    Alert,
} from "@mui/material";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../../../Redux/Store/Hooks";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import { ReactComponent as document_icon } from "../../../../Assets/Images/document_icon.svg";
import Mockexamimage from "../../../../Assets/Images/Mockexamimage.svg";
import { scheduleQuestionTypes } from "../../../../Redux/Slices/QuestionType/ScheduleQuestionTypesSlice";


const SubTopicSelection = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const dispatch = useAppDispatch();
    const { topicId } = useParams();
    const currentSubjectId = topicId || "1";
    const subjectName = location.state?.subjectName || "";

    // Get question types from Redux store
    const { data: reduxData, isLoading, error: reduxError } = useAppSelector((state) => state.ScheduleQuestionTypesSlice);

    // Fetch question types when component mounts or topicId changes
    useEffect(() => {
        if (currentSubjectId) {
            dispatch(scheduleQuestionTypes(Number(currentSubjectId)));
        }
    }, [dispatch, currentSubjectId]);

    const loading = isLoading;
    const error = reduxError ? (typeof reduxError === 'string' ? reduxError : reduxError.message || "An error occurred") : null;

    // Use API data
    const questionTypes = reduxData?.data || [];
    const subtopics = questionTypes.map((qt: any) => ({
        id: qt.id,
        title: qt.name,
        difficulty: "Medium", // Default value since API doesn't provide this
        questions: 0, // Default value since API doesn't provide this
        duration: "30 min", // Default value since API doesn't provide this
        description: qt.description || "",
        image_url: qt.image_url || "" // Add this line
    }));

    const handleMeetClick = (e: React.MouseEvent, subtopic: any) => {
        e.stopPropagation();
        const subject = subjectName || "N/A";
        navigate("/schedule-class/calendar", {
            state: {
                subject: subject,
                subjectId: Number(currentSubjectId),
                subtopic: subtopic.title,
                questionTypeId: subtopic.id
            }
        });
    };

    return (
        <Box sx={{ p: 3, display: "flex", flexDirection: "column", gap: 3 }}>
            {/* {loading && (
                <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "50vh" }}>
                    <CircularProgress />
                </Box>
            )} */}

            {error && (
                <Box sx={{ p: 3 }}>
                    <Alert severity="error">{error}</Alert>
                </Box>
            )}

            {!loading && !error && (
                <>
                    <Box>
                        <Breadcrumbs separator={<NavigateNextIcon fontSize="small" />} aria-label="breadcrumb" sx={{ mb: 2 }}>
                            <Link
                                underline="hover"
                                color="inherit"
                                href="#"
                                onClick={(e) => { e.preventDefault(); navigate("/schedule-class/topic"); }}
                                sx={{ cursor: "pointer" }}
                            >
                                Subjects
                            </Link>
                            <Typography color="text.primary">Topics</Typography>
                        </Breadcrumbs>
                        <Typography variant="h4" fontWeight="bold" sx={{ color: "#333" }}>
                            Explore Topics {subjectName ? `for ${subjectName}` : ""}
                        </Typography>
                    </Box>

                    {subtopics.length > 0 ? (
                        <Grid container spacing={3}>
                            {subtopics.map((subtopic) => (
                                <Grid item xs={12} sm={6} md={4} lg={3} key={subtopic.id}>
                                    <Card
                                        sx={{
                                            height: "100%",
                                            display: "flex",
                                            flexDirection: "column",
                                            borderRadius: "8px",
                                            boxShadow: "0 1px 7px rgba(0, 0, 0, 0.2)",
                                            transition: "transform 0.2s",
                                            "&:hover": {
                                                boxShadow: "0 4px 8px rgba(0, 0, 0, 0.2)",
                                            },
                                            cursor: "pointer",
                                        }}
                                        onClick={(e) => handleMeetClick(e, subtopic)}
                                    >
                                        <Box sx={{ position: "relative", height: "175px" }}>
                                            <img
                                                src={subtopic.image_url || Mockexamimage}
                                                alt={subtopic.title}
                                                style={{
                                                    width: "100%",
                                                    height: "100%",
                                                    objectFit: "cover",
                                                    borderRadius: "8px 8px 0 0",
                                                }}
                                            />
                                            <Box
                                                sx={{
                                                    position: "absolute",
                                                    top: "10px",
                                                    right: "10px",
                                                    background: "#22c55e",
                                                    color: "#fff",
                                                    px: "7px",
                                                    py: "1px",
                                                    borderRadius: "6px",
                                                    fontSize: "12px",
                                                    fontWeight: "500",
                                                }}
                                            >
                                                {subtopic.difficulty || "Medium"}
                                            </Box>
                                        </Box>
                                        <CardContent sx={{ flexGrow: 1, p: 2 }}>
                                            <Typography
                                                variant="h6"
                                                sx={{
                                                    fontWeight: 600,
                                                    fontSize: "18px",
                                                    mb: 1,
                                                    lineHeight: 1.3,
                                                    minHeight: "2.6em", // Consistent title height for 2 lines
                                                }}
                                            >
                                                {subtopic.title}
                                            </Typography>
                                            <Typography
                                                variant="body2"
                                                sx={{
                                                    color: "#4b5563",
                                                    fontSize: "14px",
                                                    minHeight: "4.2em", // Consistent height for 3 lines
                                                    display: "-webkit-box",
                                                    WebkitLineClamp: 3, // Allow up to 3 lines
                                                    WebkitBoxOrient: "vertical",
                                                    overflow: "hidden",
                                                    textOverflow: "ellipsis",
                                                    mb: 2,
                                                }}
                                            >
                                                {subtopic.description}
                                            </Typography>
                                            <Box sx={{ mt: "auto" }}>
                                                {/* <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
                                                    <Box sx={{ display: "flex", alignItems: "center", gap: "5px", color: "#6b7280", fontSize: "15px" }}>
                                                        <SvgIcon component={document_icon} inheritViewBox sx={{ fontSize: "16px", fill: "none" }} />
                                                        {subtopic.questions} Questions
                                                    </Box>
                                                    <Box sx={{ display: "flex", alignItems: "center", gap: "4px", color: "#6b7280", fontSize: "15px" }}>
                                                        <AccessTimeIcon sx={{ fontSize: "15px" }} />
                                                        {subtopic.duration}
                                                    </Box>
                                                </Box> */}
                                                <Button
                                                    fullWidth
                                                    variant="contained"
                                                    onClick={(e) => handleMeetClick(e, subtopic)}
                                                    sx={{
                                                        bgcolor: "#0055ff",
                                                        color: "white",
                                                        textTransform: "capitalize",
                                                        fontSize: "15px",
                                                        borderRadius: "4px",
                                                        "&:hover": { bgcolor: "#0044cc" },
                                                    }}
                                                >
                                                    Schedule Meet
                                                </Button>
                                            </Box>
                                        </CardContent>
                                    </Card>
                                </Grid>
                            ))}
                        </Grid>
                    ) : (
                        <Box sx={{ textAlign: "center", mt: 10 }}>
                            <Typography variant="h6" color="text.secondary">
                                No topics available for this subject.
                            </Typography>
                        </Box>
                    )}
                </>
            )}
        </Box>
    );
};

export default SubTopicSelection;
